import React, { useCallback, useState } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../../components/ui';
import MarkdownContent from '../../../../components/MarkdownContent';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useAiAssistantEnabled, summarizeClientHistory } from '../../../ai/useAiAssistant';

interface ClientAiBriefPanelProps {
  clientId: number;
  clientName?: string;
}

const ClientAiBriefPanel: React.FC<ClientAiBriefPanelProps> = ({ clientId, clientName }) => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const enabled = useAiAssistantEnabled();
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const text = await summarizeClientHistory(clientId);
      setSummary(text);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Could not generate client brief');
    } finally {
      setLoading(false);
    }
  }, [clientId, showError]);

  if (!enabled) return null;

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.background)}>
      <div className={cn('flex items-center justify-between gap-3 px-4 py-3 border-b', colors.border, colors.surfaceHover)}>
        <div>
          <h3 className={cn('text-sm font-semibold', colors.text)}>AI pre-call brief</h3>
          <p className={cn('text-xs mt-0.5', colors.textSecondary)}>
            Summarize history before a call{clientName ? ` with ${clientName}` : ''}.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void generate()} disabled={loading}>
          {loading ? 'Generating…' : summary ? 'Refresh' : 'Generate'}
        </Button>
      </div>
      <div className="p-4 min-h-[4rem]">
        {!summary && !loading ? (
          <p className={cn('text-sm', colors.textSecondary)}>
            Pulls appointments, todos, emails, and activity into a concise brief.
          </p>
        ) : null}
        {loading && !summary ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className={cn('h-3 rounded animate-pulse', colors.surfaceHover)} />
            ))}
          </div>
        ) : null}
        {summary ? <MarkdownContent content={summary} /> : null}
      </div>
    </div>
  );
};

export default ClientAiBriefPanel;
